import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthProvider';
import { useBootstrap } from '@/lib/api/hooks';
import { hasAuthLinkError } from '@/lib/auth/loginDestination';
import {
  PostCheckoutSetupScreen,
  clearPostCheckoutSetup,
  postCheckoutSetupActive,
} from '@/features/onboarding/postCheckoutSetup';

// Routes the patient can stay on even after onboarding_state flips to
// 'complete'. The Healthie form_answer_group webhook fires `locked` very
// soon after a portal intake submit, which advances state to 'complete';
// without this exemption RequireNotOnboarded bounces the user mid-wizard
// (e.g. as soon as they touch the consent checkbox).
const WIZARD_PATHS = new Set([
  '/onboarding/intake',
  '/onboarding/expect',
  '/onboarding/complete',
]);

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    if (postCheckoutSetupActive()) return <PostCheckoutSetupScreen />;
    return (
      <div className="min-h-screen flex items-center justify-center type-label-md text-ink-muted">
        Loading…
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{
      from: { pathname: location.pathname, search: location.search },
      authLinkError: hasAuthLinkError(location.hash),
    }} replace />;
  }

  return <>{children}</>;
}

/**
 * Full portal access may come from completed medication onboarding or an owned
 * paid lab receipt. The backend's flag never marks medication intake complete.
 */
export function RequireOnboarded({ children }: { children: React.ReactNode }) {
  const { data, isLoading, error } = useBootstrap();
  // A returning patient's checkout lands in the app, never on welcome (whose
  // gate clears the marker): the app being ready ends the setup screen, so
  // later route loaders show their normal loading state again.
  const appReady = Boolean(
    !isLoading && data && (data.portal_access_ready ?? data.onboarding_state === 'complete'),
  );
  useEffect(() => {
    if (appReady) clearPostCheckoutSetup();
  }, [appReady]);

  if (isLoading) {
    if (postCheckoutSetupActive()) return <PostCheckoutSetupScreen />;
    return (
      <div className="min-h-screen flex items-center justify-center type-label-md text-ink-muted">
        Loading…
      </div>
    );
  }

  // If bootstrap itself failed (e.g., JWT just expired), fall through; RequireAuth
  // will catch the next render.
  if (error || !data) return <>{children}</>;

  if (!(data.portal_access_ready ?? data.onboarding_state === 'complete')) {
    // Everything not-complete funnels through the welcome drawer — it
    // adapts its CTA ("Start intake" vs "Continue") to whatever steps
    // remain (intakes / ID / consent). Routing intake_submitted to the
    // static /onboarding/expect page dead-ended patients whose intake was
    // already on file (web-mirrored intakes) in an expect→complete→expect
    // loop with no path into the ID + consent steps.
    return <Navigate to="/onboarding/welcome" replace />;
  }

  return <>{children}</>;
}

/**
 * Which bootstrap rows reach which surface. Mirrors ADMIN_SURFACE_ROLES /
 * CLINICAL_SURFACE_ROLES in app/deps.py — keep them in step.
 *
 * `is_admin` alone is NOT enough: it is true for 'clinical' too, and a
 * clinician must never see billing, refunds or lifecycle campaigns. A
 * legacy NULL admin_role on an elevated row reads as full 'admin', same as
 * the backend.
 */
const ADMIN_SURFACE_ROLES = ['admin', 'support', null] as const;
const CLINICAL_SURFACE_ROLES = [
  'admin',
  'clinical',
  'clinical_supervisor',
  null,
] as const;

function reaches(
  data: { is_admin: boolean; admin_role: string | null } | undefined,
  allowed: readonly (string | null)[],
): boolean {
  if (!data?.is_admin) return false;
  return allowed.includes(data.admin_role ?? null);
}

function Loading() {
  if (postCheckoutSetupActive()) return <PostCheckoutSetupScreen />;
  return (
    <div className="min-h-screen flex items-center justify-center type-label-md text-ink-muted">
      Loading…
    </div>
  );
}

/**
 * Gates `/admin/*`. Backend enforces via `require_admin` on every /admin
 * API — this client gate is purely a UX convenience (avoids flashing the
 * patient UI, hides routes). Treat it as non-security.
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useBootstrap();

  if (isLoading) return <Loading />;

  if (!reaches(data, ADMIN_SURFACE_ROLES)) {
    // A clinician landing here goes to their own portal rather than the
    // patient app — otherwise a stale /admin bookmark dead-ends them.
    return (
      <Navigate
        to={
          reaches(data, CLINICAL_SURFACE_ROLES) ? '/clinician-portal' : '/app'
        }
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * Full-admin-only UX gate for cross-system directory surfaces. Backend APIs
 * and provider memberships remain the security boundaries. A legacy elevated
 * row with a null role is treated as full admin, matching app/deps.py.
 */
export function RequireFullAdmin({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useBootstrap();

  if (isLoading) return <Loading />;

  if (
    !data?.is_admin ||
    (data.admin_role !== 'admin' && data.admin_role !== null)
  ) {
    return (
      <Navigate
        to={reaches(data, ADMIN_SURFACE_ROLES) ? '/admin' : '/app'}
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * Gates `/clinician-portal/*`. Same non-security caveat as RequireAdmin —
 * `require_clinical` is the real gate.
 */
export function RequireClinical({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useBootstrap();

  if (isLoading) return <Loading />;

  if (!reaches(data, CLINICAL_SURFACE_ROLES)) {
    return (
      <Navigate
        to={reaches(data, ADMIN_SURFACE_ROLES) ? '/admin' : '/app'}
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * Inverse guard — if already `complete`, skip onboarding entirely and jump
 * to `/app`. Prevents confused users from revisiting the wizard.
 *
 * EXCEPT for in-flight wizard paths (intake, expect, complete). Healthie's
 * form_answer_group webhook fires `locked` within seconds of a portal
 * intake submit, which advances state to 'complete' before the patient
 * has finished the consent screen. Bouncing them mid-wizard breaks the
 * UX (clicking a consent checkbox triggers a bootstrap refetch which
 * picks up the new 'complete' state). The wizard handles its own exit
 * via navigate(exitRoute, { replace: true }) once the consent step
 * submits.
 */
export function RequireNotOnboarded({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data, isLoading } = useBootstrap();
  const location = useLocation();

  if (isLoading) {
    if (postCheckoutSetupActive()) return <PostCheckoutSetupScreen />;
    return (
      <div className="min-h-screen flex items-center justify-center type-label-md text-ink-muted">
        Loading…
      </div>
    );
  }

  if (
    (data?.portal_access_ready ?? data?.onboarding_state === 'complete') &&
    !WIZARD_PATHS.has(location.pathname)
  ) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
